import { TypedDocumentNode, gql } from '@apollo/client';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ButtonProps } from '@sorare/core/src/atoms/buttons/ButtonBase';
import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { glossary } from '@sorare/core/src/lib/glossary';

import { FixtureStatus } from 'components/fixture/FixtureStatus';

import {
  LeaguePageHeader_so5LeaderboardContender,
  LeaguePageHeader_so5League,
} from './__generated__/index.graphql';

const Title = styled.div`
  > *:first-child {
    font: var(--t-label-s);
  }
  > *:only-child,
  > *:nth-child(2) {
    font: var(--t-bold) var(--t-label-l);
  }
`;
const LeagueLogo = styled.img`
  object-fit: contain;
`;

const Right = styled(Horizontal)`
  margin-left: auto;
`;
const Status = styled.div`
  margin-left: auto;
`;

export type Props = {
  so5League: LeaguePageHeader_so5League;
  so5LeaderboardContender?: LeaguePageHeader_so5LeaderboardContender;
  title?: ReactNode;
  onClose?: Pick<ButtonProps, 'onClick' | 'to' | 'disabled' | 'viewTransition'>;
  displayFixtureStatus?: boolean;
  action?: ReactNode;
};

export const LeaguePageHeader = ({
  so5League,
  so5LeaderboardContender,
  title,
  onClose,
  displayFixtureStatus,
  action,
}: Props) => {
  const { formatMessage } = useIntl();
  const [fixtureDetailsOpen, setFixtureDetailsOpen] = useState(false);

  return (
    <Horizontal>
      {onClose && (
        <IconButton
          color="transparent"
          icon={faTimes}
          title={formatMessage(glossary.close)}
          {...onClose}
        />
      )}
      <LeagueLogo src={so5League?.iconUrl} alt="" width="40px" height="40px" />
      <Title>
        <h2>
          <Ellipsis>{so5League?.displayName}</Ellipsis>
        </h2>
        {title && <h3>{title}</h3>}
      </Title>
      <Right>
        {displayFixtureStatus && (
          <Status>
            <OnboardingHighlightWrapper highlightName="ProSchedule">
              <FixtureStatus
                so5Fixture={so5League.so5Fixture}
                so5Lineup={so5LeaderboardContender?.so5Lineup || undefined}
                detailsOpen={fixtureDetailsOpen}
                setDetailsOpen={setFixtureDetailsOpen}
              />
            </OnboardingHighlightWrapper>
          </Status>
        )}
        {action}
      </Right>
    </Horizontal>
  );
};

LeaguePageHeader.fragments = {
  so5League: gql`
    fragment LeaguePageHeader_so5League on So5League {
      slug
      displayName
      iconUrl
      so5Fixture {
        slug
        aasmState
        ...FixtureStatus_so5Fixture
      }
    }
    ${FixtureStatus.fragments.so5Fixture}
  ` as TypedDocumentNode<LeaguePageHeader_so5League>,
  so5LeaderboardContender: gql`
    fragment LeaguePageHeader_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        ...FixtureStatus_so5Lineup
      }
    }
    ${FixtureStatus.fragments.so5Lineup}
  ` as TypedDocumentNode<LeaguePageHeader_so5LeaderboardContender>,
};
